import "react-datepicker/dist/react-datepicker.css";
import { useMemo, useState } from "react";
import { ReportApi } from "src/services/api/ReportApi";
import {
  ColDef,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  LoadSuccessParams,
  GridApi,
  SizeColumnsToContentStrategy,
  ValueFormatterParams,
  ValueFormatterFunc,
} from "ag-grid-enterprise";

import useSidebar from "src/hooks/useSidebar";
import useReport from "src/hooks/useReport";

import BaseGrid from "../../BaseGrid";
import LoadingComponent from "src/components/LoadingComponent";
import SearchCriteriaRedReports from "src/components/SearchCriteriaRedReports";
import { downloadFile } from "src/utils/DownloadFile";
import { formatCurrency } from "src/utils/FormatCurrency";
import { CardholderFeeSummaryDetailModel } from "src/models/CardholderFeeSummaryDetailModel";

const CardholderFeeSummary = () => {
  const { setIsOpen } = useSidebar();
  const { reportInputs, mapReportRequest, getContextMenuItems } = useReport();

  const [gridReady, setGridReady] = useState(false);
  const [gridApi, setGridApi] = useState<GridApi>();
  const [dataSource, setDataSource] = useState<IServerSideDatasource>();
  const [isDownload, setIsDownload] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const currencyFormatter: ValueFormatterFunc<CardholderFeeSummaryDetailModel, any> = (
    params: ValueFormatterParams<CardholderFeeSummaryDetailModel, any>
  ): string => {
    const v = params?.value;
    if (v === null || v === undefined) return "";
    const n = typeof v === "number" ? v : Number(v);
    if (Number.isNaN(n)) return "";
    const alphaCode = params.data?.transactionCurrency ?? params.data?.binCurrency ?? "GBP";
    return String(formatCurrency(n, alphaCode));
  };

  const [colDefs] = useState<ColDef<CardholderFeeSummaryDetailModel>[]>([
    { headerName: "Program Name", field: "programName" },
    { headerName: "Activating Merchant Group", field: "activatingMerchantGroup" },
    { headerName: "Cardholder Billing Amount", field: "cardholderBillingAmount", valueFormatter: currencyFormatter },
    { headerName: "Fee Type", field: "feeType" },
    { headerName: "Fee Amount", field: "feeAmount", valueFormatter: currencyFormatter },
    { headerName: "Transaction Count", field: "transactionCount" },
    { headerName: "Transaction Merchant", field: "transactionMerchant" },
    { headerName: "Transaction Currency", field: "transactionCurrency" },
    { headerName: "Original Transaction Amount", field: "originalTransactionAmount", valueFormatter: currencyFormatter },
    { headerName: "Issuing Bank", field: "issuingBank" },
    { headerName: "ICA", field: "ica" },
    { headerName: "Bin", field: "bin" },
    { headerName: "BIN Description", field: "binDescription" },
    { headerName: "BIN Currency", field: "binCurrency" },
  ]);

  const defaultColDef: ColDef = {
    sortable: false,
    filter: false,
    cellStyle: { textAlign: "center" },
  };

  const autoSizeStrategy = useMemo<SizeColumnsToContentStrategy>(() => {
    return {
      type: "fitCellContents",
      defaultMinWidth: 120,
      defaultMaxWidth: 300,
      skipHeader: false,
    };
  }, []);

  const handleViewReport = () => {
    setIsDownload(false);
    setGridReady(true);
    setDataSource(createServerSideDatasource());
  };

  const handleDownloadReport = () => {
    setIsDownload(true);
    setIsLoading(true);
    ReportApi.exportCardholderFeeSummary(mapReportRequest(null))
      .then((res: any) => {
        downloadFile(CardholderFeeSummary.displayName!, res.data);
      })
      .finally(() => setIsLoading(false));
  };

  const createServerSideDatasource = (): IServerSideDatasource => {
    return {
      getRows: (params: IServerSideGetRowsParams) => {
        getReportData(params);
      },
    };
  };

  const getReportData = (params: IServerSideGetRowsParams | null) => {
    setIsOpen(false);
    ReportApi.getCardholderFeeSummary(mapReportRequest(params))
      .then((res: any) => {
        const result = res.data;
        const success: LoadSuccessParams = {
          rowData: result.rows,
          rowCount: result.totalCount,
        };
        if (result?.rows?.length > 0) {
          params?.api?.hideOverlay();
          params?.success(success);
        } else {
          params?.api?.showNoRowsOverlay();
          params?.success({ rowData: [], rowCount: 0 });
        }

        // Add pinned bottom totals row if summary detail provided
        try {
          const sd: any = result?.summaryDetail ?? {};
          const ta = sd?.totalAmount;
          let totalFee: number | null = null;
          let totalOriginal: number | null = null;
          let totalTxnCount: number | null = null;

          if (Array.isArray(ta) && ta.length > 0) {
            totalFee = ta[0]?.totalFeeAmount ?? ta[0]?.TotalFeeAmount ?? null;
            totalOriginal = ta[0]?.totalOriginalTransactionAmount ?? ta[0]?.TotalOriginalTransactionAmount ?? null;
            totalTxnCount = ta[0]?.totalTransactionCount ?? ta[0]?.TotalTransactionCount ?? null;
          } else if (ta) {
            totalFee = ta?.totalFeeAmount ?? ta?.TotalFeeAmount ?? null;
            totalOriginal = ta?.totalOriginalTransactionAmount ?? ta?.TotalOriginalTransactionAmount ?? null;
            totalTxnCount = ta?.totalTransactionCount ?? ta?.TotalTransactionCount ?? null;
          }

          if (totalFee != null || totalOriginal != null || totalTxnCount != null) {
            const totalsRow: Partial<CardholderFeeSummaryDetailModel> = {
              programName: "Totals",
              feeAmount: totalFee ?? undefined,
              transactionCount: totalTxnCount ?? undefined,
              originalTransactionAmount: totalOriginal ?? undefined,
            };

            ((params as any)?.api ?? (gridApi as any))?.setGridOption?.("pinnedBottomRowData", [totalsRow as any]);
          } else {
            ((params as any)?.api ?? (gridApi as any))?.setGridOption?.("pinnedBottomRowData", []);
          }
        } catch {
          // ignore totals parsing errors
        }
      })
      .catch(() => params?.fail?.());
  };

  return (
    <>
      <div className="report-container">
        <div className="report-header">
          <h5 className="mb-0">Cardholder Fee Summary Report</h5>
        </div>
        <div className="blocks">
          <SearchCriteriaRedReports
            handleViewReport={handleViewReport}
            handleDownloadReport={handleDownloadReport}
            useBin={true}
            usePrograms={true}
            showToDate={true}
          />
        </div>
        {isDownload && isLoading ? (
          <LoadingComponent />
        ) : (
          !isDownload && (
            <BaseGrid
              colDefs={colDefs}
              defaultColDef={defaultColDef}
              getData={getReportData}
              isGridReady={gridReady}
              reportInputs={reportInputs}
              dataSource={dataSource}
              gridApi={gridApi}
              setGridApi={setGridApi}
              getContextMenuItems={getContextMenuItems}
              autoSizeStrategy={autoSizeStrategy}
            />
          )
        )}
      </div>
    </>
  );
};

CardholderFeeSummary.displayName = "CardholderFeeSummary";

export default CardholderFeeSummary;
